import type {
  RichTextEditorProps,
  ActionButtonProps,
} from 'src/types/sense-ai-panel';
import * as React from 'react';
import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import {debounce} from 'lodash';
import {Map} from 'immutable';

import {
  Editor,
  EditorState,
  convertFromRaw,
  convertToRaw,
  RichUtils,
  ContentState,
  Modifier,
  CompositeDecorator,
  getDefaultKeyBinding,
  KeyBindingUtil,
  Entity,
  type DraftEditorCommand,
} from 'draft-js';

import {stateToHTML} from 'draft-js-export-html';
import {stateFromHTML} from 'draft-js-import-html';
import {classify} from '@spaced-out/ui-design-system/lib/utils/classify';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {
  FormLabelSmall,
  SubTitleLarge,
  ButtonTextMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Menu} from '@spaced-out/ui-design-system/lib/components/Menu';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {colorBackgroundPrimary} from '@spaced-out/ui-design-system/lib/styles/variables/_color';

import {PLACEHOLDER_LIST} from 'src/utils/crm';

import css from './rich-text-editor.css';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';

/** Caret `getBoundingClientRect()` for Floating UI virtual reference (call each compute cycle). */
function getCaretBoundingClientRect(): DOMRect | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) {
    return null;
  }
  const range = sel.getRangeAt(0);
  if (!range.collapsed) {
    return null;
  }
  let rect = range.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    const rects = range.getClientRects();
    if (rects.length > 0) {
      rect = rects[0] as DOMRect;
    }
  }
  if (rect.width === 0 && rect.height === 0) {
    return null;
  }
  return rect;
}

type ButtonData = {
  id: string;
  ariaLabel: string;
  iconRightName: string;
  onClick: () => void;
  tooltipContent: string;
};

const ActionButton = ({
  tooltipContent,
  onClick,
  ...props
}: ActionButtonProps) => (
  <Tooltip
    body={<span>{tooltipContent}</span>}
    placement="top"
    delayMotionDuration="fast"
  >
    <Button
      {...props}
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
      size="small"
      type="ghost"
    />
  </Tooltip>
);

const RichTextEditor = ({
  classNames,
  value,
  height = '178px',
  bgColor = colorBackgroundPrimary,
  borderRadius = '8px',
  mentionBorderRadius = '0px',
  placeholder,
  onChange,
  disabled = false,
  label,
  handleButtonAction = () => {},
  required = false,
  showActionButton = false,
  actionButtonText = 'Action',
  actionButtonType = 'secondary',
  actionButtonIcon = '',
  readOnly = false,
  isDataLoading = false,
  isPlaceholderEnabled = false,
  isMentionEnabled = false,
  onMentionSearch,
  showActionsButtons,
  testId,
}: RichTextEditorProps) => {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const editorRef = React.useRef();
  const draftEditorContainerRef = React.useRef<HTMLDivElement | null>(null);
  /** Last caret while @ is active; used when blur moves selection away (e.g. mousedown on menu). */
  const mentionAnchorRectRef = React.useRef<DOMRect | null>(null);

  // Helper functions for decorators
  const findLinkEntities = (
    // @ts-expect-error TS2304
    contentBlock: BlockNodeRecord,
    callback: (start: number, end: number) => void,
    contentState: ContentState,
  ) => {
    // @ts-expect-error - TS7006 - Parameter 'character' implicitly has an 'any' type.
    contentBlock.findEntityRanges((character) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'LINK'
      );
    }, callback);
  };

  const Link = (props: any) => {
    const {url} = props.contentState.getEntity(props.entityKey).getData();
    return (
      <a
        href={url}
        className={css.link}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={generateTestId({
          base: testId,
          slot: 'rich-text-editor-link',
          index: props.entityKey,
        })}
      >
        {props.children}
      </a>
    );
  };

  const findPlaceholder = (
    // @ts-expect-error TS2304
    contentBlock: BlockNodeRecord,
    callback: (start: number, end: number) => void,
  ) => {
    const text = contentBlock.getText();
    const regex = /{{(.*?)}}/g; // Matches placeholders inside {{ }}
    let matchArr;
    while ((matchArr = regex.exec(text)) !== null) {
      if (matchArr && matchArr.index !== undefined && matchArr[0]) {
        callback(matchArr.index, matchArr.index + matchArr[0].length);
      }
    }
  };

  const PlaceholderSpan = (props: any) => {
    return (
      <span
        className={css.placeholderSpan}
        data-offset-key={props.offsetKey}
        data-testid={generateTestId({
          base: testId,
          slot: 'rich-text-editor-placeholder-span',
          index: props.offsetKey,
        })}
      >
        {props.children}
      </span>
    );
  };

  // Modify the findMention decorator function to handle spaces in names
  const findMention = (
    // @ts-expect-error TS2304
    contentBlock: BlockNodeRecord,
    callback: (start: number, end: number) => void,
  ) => {
    const text = contentBlock.getText();
    const regex = /@([^(]+)\((\d+)\)/g; // This regex already allows spaces in names
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match && match.index !== undefined && match[0]) {
        callback(match.index, match.index + match[0].length);
      }
    }
  };

  // Update the MentionSpan component to handle spaces properly
  const MentionSpan = (props: any) => {
    // Get text content
    const textContent = props.children[0]?.props?.text || props.children;
    // Extract just the name from @[name](id) format
    const match =
      typeof textContent === 'string' &&
      textContent.match(/@\[([^\]]+)\]\((\d+)\)/);

    if (match) {
      const displayName = '@' + match[1].trim();

      return (
        <span
          className={css.placeholderSpan}
          data-offset-key={props.offsetKey}
          contentEditable={false} // Make mention non-editable as a unit
          data-testid={generateTestId({
            base: testId,
            slot: 'rich-text-editor-mention-span',
            index: props.offsetKey,
          })}
        >
          {displayName}
        </span>
      );
    }

    return (
      <span
        className={css.placeholderSpan}
        data-offset-key={props.offsetKey}
        data-testid={generateTestId({
          base: testId,
          slot: 'rich-text-editor-mention-span',
          index: props.offsetKey,
        })}
      >
        {props.children}
      </span>
    );
  };

  // Update the handleMentionSelect function to improve cursor positioning
  const handleMentionSelect = (option: {
    key: string;
    label: string;
    fullName: string;
  }) => {
    if (!editorState || !option) {
      return;
    }

    const contentState = editorState.getCurrentContent();
    if (!contentState) {
      return;
    }

    const selection = editorState.getSelection();
    if (!selection) {
      return;
    }

    const blockKey = selection.getAnchorKey();
    if (!blockKey) {
      return;
    }

    const block = contentState.getBlockForKey(blockKey);
    if (!block) {
      return;
    }

    const text = block.getText();
    const cursorPosition = selection.getStartOffset();

    // Find the @mention at cursor position - updated to match mentions anywhere in text
    const regex = /@(\w*)/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match && match.index !== undefined) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;
        if (cursorPosition >= matchStart && cursorPosition <= matchEnd) {
          // Replace @username with the mention using new format @[name] (id)
          const mentionText = `@[${option.fullName}](${option.key})`;

          // Create an entity for the mention
          const contentStateWithEntity = contentState.createEntity(
            'MENTION',
            'IMMUTABLE',
            {
              userId: option.key,
              name: option.fullName,
            },
          );

          const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

          // Replace text and apply entity
          let newContentState = Modifier.replaceText(
            contentStateWithEntity,
            selection.merge({
              anchorOffset: matchStart,
              focusOffset: matchEnd,
            }),
            mentionText,
            // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'DraftInlineStyle | undefined'.
            null,
            entityKey,
          );

          // Add a space after the mention
          newContentState = Modifier.insertText(
            newContentState,
            newContentState.getSelectionAfter(),
            ' ',
          );

          // Push the changes to editor state
          const newEditorState = EditorState.push(
            editorState,
            newContentState,
            'insert-characters',
          );

          // Move cursor to after the inserted space
          const newSelection = newEditorState.getSelection().merge({
            anchorOffset: matchStart + mentionText.length + 1,
            focusOffset: matchStart + mentionText.length + 1,
          });

          // Force selection at the new position
          const finalEditorState = EditorState.forceSelection(
            newEditorState,
            newSelection,
          );

          handleDraftChange(finalEditorState);
          setShowMentionDropdown(false);
          setMentionSearchText('');
          setMentionUsers([]);
          return;
        }
      }
    }
  };

  // Update createDecorator to include mention decorator
  const createDecorator = React.useMemo(
    () =>
      new CompositeDecorator([
        {
          strategy: findPlaceholder,
          component: PlaceholderSpan,
        },
        {
          strategy: findMention,
          component: MentionSpan,
        },
        {
          strategy: findLinkEntities,
          component: Link,
        },
      ]),
    [],
  );

  const [editorState, setEditorState] = React.useState(() =>
    EditorState.createEmpty(createDecorator),
  );
  const [textAlign, setTextAlign] = React.useState<'left' | 'center' | 'right'>(
    'left',
  );

  const [isLinkModalOpen, setIsLinkModalOpen] = React.useState(false);
  const [linkTextValue, setLinkTextValue] = React.useState('');

  const [showMentionDropdown, setShowMentionDropdown] = React.useState(false);
  const [mentionUsers, setMentionUsers] = React.useState([]);
  const [mentionSearchText, setMentionSearchText] = React.useState('');

  const isMentionOpen = showMentionDropdown && isMentionEnabled;

  const {refs, floatingStyles, update} = useFloating({
    open: isMentionOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(4),
      flip({
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
        padding: 8,
      }),
      shift({padding: 8}),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Helper to check if cursor is inside a placeholder
  const isInsidePlaceholder = () => {
    const selection = editorState.getSelection();
    if (selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const blockKey = selection.getAnchorKey();
      const block = contentState.getBlockForKey(blockKey);
      const text = block ? block.getText() : '';
      const cursorPosition = selection.getStartOffset();

      const regex = /{{(.*?)}}/g;
      let match;
      while ((match = regex.exec(text)) !== null) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;
        if (cursorPosition >= matchStart && cursorPosition <= matchEnd) {
          return true;
        }
      }
    }
    return false;
  };

  // Helper to check if cursor is inside a selected mention
  const isInsideSelectedMention = () => {
    const selection = editorState.getSelection();
    if (!selection || !selection.isCollapsed()) {
      return false;
    }

    const contentState = editorState.getCurrentContent();
    if (!contentState) {
      return false;
    }

    const blockKey = selection.getAnchorKey();
    if (!blockKey) {
      return false;
    }

    const block = contentState.getBlockForKey(blockKey);
    if (!block) {
      return false;
    }

    const text = block.getText();
    const cursorPosition = selection.getStartOffset();

    const regex = /@([^(]+)\((\d+)\)/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match && match.index !== undefined) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;
        if (cursorPosition >= matchStart && cursorPosition <= matchEnd) {
          return {
            start: matchStart,
            end: matchEnd,
          };
        }
      }
    }
    return false;
  };

  // Improved insert text function that maintains cursor position and handles null cases
  const insertTextAtFocus = (editorState: EditorState, text: string) => {
    if (!editorState || !text) {
      console.warn('Invalid editorState or text in insertTextAtFocus');
      return editorState;
    }

    try {
      const contentState = editorState.getCurrentContent();
      const selectionState = editorState.getSelection();

      if (!contentState || !selectionState) {
        console.warn(
          'Invalid contentState or selectionState in insertTextAtFocus',
        );
        return editorState;
      }

      const newContentState = Modifier.replaceText(
        contentState,
        selectionState,
        text,
      );

      if (!newContentState) {
        console.warn('Failed to create new content state in insertTextAtFocus');
        return editorState;
      }

      return EditorState.push(
        editorState,
        newContentState,
        'insert-characters',
      );
    } catch (error: any) {
      console.error('Error in insertTextAtFocus:', error);
      return editorState;
    }
  };

  // Improved handler that properly updates state and notifies parent
  const handleDraftChange = (newEditorState: EditorState) => {
    if (
      editorState.getCurrentContent() !== newEditorState.getCurrentContent()
    ) {
      const contentState = newEditorState.getCurrentContent();
      const content = convertToRaw(contentState);
      const contentToPass = JSON.stringify(content);
      onChange?.(contentToPass);
    }
    // Always update the editor state
    setEditorState(newEditorState);
  };

  const toggleBlockType = (blockType: string) => {
    handleDraftChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const handleStyle = (style: string) => {
    handleDraftChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const handleHeader = (
    headerType: 'header-one' | 'header-two' | 'header-three',
  ) => {
    handleDraftChange(RichUtils.toggleBlockType(editorState, headerType));
  };

  const handleList = (
    listType: 'unordered-list-item' | 'ordered-list-item',
  ) => {
    handleDraftChange(RichUtils.toggleBlockType(editorState, listType));
  };

  const createButtonData = ({
    id,
    label,
    icon,
    handler,
    content,
  }: {
    id: string;
    label: string;
    icon: string;
    handler: () => void;
    content: string;
  }): ButtonData => ({
    id,
    ariaLabel: `${label} Button`,
    iconRightName: icon,
    onClick: handler,
    tooltipContent: content,
  });

  const promptForLink = () => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const startKey = editorState.getSelection().getStartKey();
      const startOffset = editorState.getSelection().getStartOffset();
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);

      let url = '';
      if (linkKey) {
        const linkInstance = contentState.getEntity(linkKey);
        url = linkInstance.getData().url;
      }

      setIsLinkModalOpen(true);
      setLinkTextValue(url);
    }
  };

  const actionButtonsData: Array<ButtonData> = [
    createButtonData({
      id: 'bold',
      label: 'Bold',
      icon: 'bold',
      handler: () => handleStyle('BOLD'),
      content: 'Bold',
    }),
    createButtonData({
      id: 'italic',
      label: 'Italic',
      icon: 'italic',
      handler: () => handleStyle('ITALIC'),
      content: 'Italic',
    }),
    createButtonData({
      id: 'underline',
      label: 'Underline',
      icon: 'underline',
      handler: () => handleStyle('UNDERLINE'),
      content: 'Underline',
    }),
    createButtonData({
      id: 'h1',
      label: 'H1',
      icon: 'h1',
      handler: () => handleHeader('header-one'),
      content: 'H1',
    }),
    createButtonData({
      id: 'h2',
      label: 'H2',
      icon: 'h2',
      handler: () => handleHeader('header-two'),
      content: 'H2',
    }),
    createButtonData({
      id: 'h3',
      label: 'H3',
      icon: 'h3',
      handler: () => handleHeader('header-three'),
      content: 'H3',
    }),
    createButtonData({
      id: 'unorderedList',
      label: 'UL',
      icon: 'list-ul',
      handler: () => handleList('unordered-list-item'),
      content: 'Unordered List',
    }),
    createButtonData({
      id: 'orderedList',
      label: 'OL',
      icon: 'list-ol',
      handler: () => handleList('ordered-list-item'),
      content: 'Ordered List',
    }),
    createButtonData({
      id: 'blockquote',
      label: 'Qoute',
      icon: 'quote-right',
      handler: () => toggleBlockType('blockquote'),
      content: 'Qoute',
    }),
    createButtonData({
      id: 'link',
      label: 'Link',
      icon: 'link',
      handler: () => promptForLink(),
      content: 'Link',
    }),
  ];

  const visibleActionButtons = showActionsButtons
    ? actionButtonsData.filter((button) =>
        showActionsButtons.includes(button.id),
      )
    : actionButtonsData;

  const onFocus = () => {
    const editor = editorRef.current;
    if (editor) {
      // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
      editor.focus();
    }
  };
  // TODO(lakshay) :will remove after testing
  // const convertHtmlToText = (html: string) => {
  //   const formattedText = html
  //     .replace(/<br\s*\/?>/gi, '\n') // Replace <br> with newline
  //     .replace(/<\/div>/gi, '\n') // Replace </div> with newline
  //     .replace(/<div[^>]*>/gi, '') // Remove <div> tags
  //     .replace(/<b>(.*?)<\/b>/gi, '**$1**') // Optional: Handle <b> to markdown-like for bold
  //     .replace(/<i>(.*?)<\/i>/gi, '_$1_') // Optional: Handle <i> to markdown-like for italic
  //     .replace(/<u>(.*?)<\/u>/gi, '__$1__') // Optional: Handle <u> to markdown-like for underline
  //     .replace(/<\/?[^>]+(>|$)/g, ''); // Remove other tags

  //   return formattedText;
  // };

  // Initialize editor content from props
  React.useEffect(() => {
    if (!value) {
      setEditorState(EditorState.createEmpty(createDecorator));
      return;
    }

    try {
      // First try parsing as JSON (raw Draft.js format)
      const contentState = convertFromRaw(JSON.parse(value));
      const newEditorState = EditorState.createWithContent(
        contentState,
        createDecorator,
      );
      // Maintain the selection/focus from the previous state
      const currentSelection = editorState.getSelection();
      const finalEditorState = EditorState.forceSelection(
        newEditorState,
        currentSelection,
      );
      setEditorState(finalEditorState);
    } catch (error: any) {
      try {
        // If parsing as JSON fails, try converting from HTML
        // (preserves underline/link styles, avoids literal "__...__" text)
        const contentState = stateFromHTML(value);
        const newEditorState = EditorState.createWithContent(
          contentState,
          createDecorator,
        );
        // Maintain the selection/focus from the previous state
        const currentSelection = editorState.getSelection();
        const finalEditorState = EditorState.forceSelection(
          newEditorState,
          currentSelection,
        );
        setEditorState(finalEditorState);
      } catch (innerError: any) {
        console.error('Failed to initialize editor content:', innerError);
        setEditorState(EditorState.createEmpty(createDecorator));
      }
    }
  }, [value]);

  // Enhance handleKeyCommand to better handle mentions
  const handleKeyCommand = (
    command: DraftEditorCommand | string,
    editorState: EditorState,
  ) => {
    if (command === 'delete-placeholder') {
      const selection = editorState.getSelection();
      const contentState = editorState.getCurrentContent();
      const blockKey = selection.getAnchorKey();
      const block = contentState.getBlockForKey(blockKey);
      const text = block.getText();
      const cursorPosition = selection.getStartOffset();

      // Check for placeholder first
      const placeholderRegex = /{{(.*?)}}/g;
      let match;
      while ((match = placeholderRegex.exec(text)) !== null) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;

        if (cursorPosition >= matchStart && cursorPosition <= matchEnd) {
          // Delete the whole placeholder
          const newContentState = Modifier.replaceText(
            contentState,
            selection.merge({
              anchorOffset: matchStart,
              focusOffset: matchEnd,
            }),
            '',
          );

          handleDraftChange(
            EditorState.push(editorState, newContentState, 'remove-range'),
          );
          return 'handled';
        }
      }

      // Check for mention with improved regex (handling spaces)
      const mentionRegex = /@([^(]+)\((\d+)\)/g;
      while ((match = mentionRegex.exec(text)) !== null) {
        if (match && match.index !== undefined) {
          const matchStart = match.index;
          const matchEnd = matchStart + match[0].length;

          if (cursorPosition > matchStart && cursorPosition <= matchEnd) {
            // Delete the whole mention
            const newContentState = Modifier.replaceText(
              contentState,
              selection.merge({
                anchorOffset: matchStart,
                focusOffset: matchEnd,
              }),
              '',
            );

            handleDraftChange(
              EditorState.push(editorState, newContentState, 'remove-range'),
            );
            return 'handled';
          }
        }
      }
    }

    // Let RichUtils handle standard commands
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleDraftChange(newState);
      return 'handled';
    }

    return 'not-handled';
  };

  // Update keyBindingFn to handle both placeholder and mention deletion
  const keyBindingFn = (e: React.KeyboardEvent) => {
    if (
      (e.key === 'Backspace' || e.key === 'Delete') &&
      (isInsidePlaceholder() || isInsideSelectedMention())
    ) {
      return 'delete-placeholder';
    }
    return getDefaultKeyBinding(e);
  };

  // Update handleBeforeInput to handle both placeholder and mention deletion
  const handleBeforeInput = (chars: string, editorState: EditorState) => {
    if (!editorState) {
      return 'not-handled';
    }

    // For normal typing, let Draft.js handle it
    if (chars !== 'Backspace' && chars !== 'Delete') {
      return 'not-handled';
    }

    // Only handle backspace/delete specially when inside a placeholder or mention
    if (isInsidePlaceholder() || isInsideSelectedMention()) {
      return 'handled';
    }

    return 'not-handled';
  };

  // @ts-expect-error - TS7006 - Parameter 'string' implicitly has an 'any' type.
  function isValidUrl(string) {
    if (!string) {
      return false;
    }

    const urlPattern =
      /^(http:\/\/|https:\/\/|www\.)?[\w-]+(\.[a-zA-Z0-9-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;
    return urlPattern.test(string);
  }

  const confirmSelectedLink = (
    e:
      | React.SyntheticEvent<HTMLElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const url = linkTextValue;
    if (!url || !isValidUrl(url)) {
      return;
    }

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'LINK',
      'MUTABLE',
      {url},
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const selection = editorState.getSelection();
    const newContentState = Modifier.applyEntity(
      contentStateWithEntity,
      selection,
      entityKey,
    );

    // Apply the entity and update state
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'apply-entity',
    );

    // Maintain focus and selection after applying the entity
    const updatedEditorState = EditorState.forceSelection(
      newEditorState,
      selection,
    );

    handleDraftChange(updatedEditorState);
    setIsLinkModalOpen(false);
    setLinkTextValue('');
  };

  const onLinkInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.which === 13) {
      confirmSelectedLink(e);
    }
  };

  const handleCloseLinkModal = () => {
    setLinkTextValue('');
    setIsLinkModalOpen(false);
  };

  // Effect to handle mention search
  React.useEffect(() => {
    if (isMentionEnabled && showMentionDropdown && onMentionSearch) {
      const searchMentions = async () => {
        try {
          const users = await onMentionSearch(mentionSearchText);
          // @ts-expect-error - TS2345 - Argument of type '{ id: string; label: string; fullName: string; }[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
          setMentionUsers(users);
        } catch (error: any) {
          console.error('Error searching mentions:', error);
          setMentionUsers([]);
        }
      };
      searchMentions();
    }
  }, [
    mentionSearchText,
    showMentionDropdown,
    isMentionEnabled,
    onMentionSearch,
  ]);

  // Effect to show/hide mention dropdown
  React.useEffect(() => {
    if (!editorState) {
      return;
    }

    const selection = editorState.getSelection();
    if (!selection || !selection.isCollapsed()) {
      setShowMentionDropdown(false);
      setMentionSearchText('');
      return;
    }

    const contentState = editorState.getCurrentContent();
    if (!contentState) {
      setShowMentionDropdown(false);
      setMentionSearchText('');
      return;
    }

    const blockKey = selection.getAnchorKey();
    if (!blockKey) {
      setShowMentionDropdown(false);
      setMentionSearchText('');
      return;
    }

    const block = contentState.getBlockForKey(blockKey);
    if (!block) {
      setShowMentionDropdown(false);
      setMentionSearchText('');
      return;
    }

    const text = block.getText();
    const cursorPosition = selection.getStartOffset();

    // Updated regex to match @mentions anywhere in text, not just at the end
    const regex = /@(\w*)/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match && match.index !== undefined) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;
        if (cursorPosition >= matchStart && cursorPosition <= matchEnd) {
          setShowMentionDropdown(true);
          setMentionSearchText(match[1] ?? '');
          return;
        }
      }
    }
    setShowMentionDropdown(false);
    setMentionSearchText('');
  }, [editorState, isMentionEnabled]);

  React.useLayoutEffect(() => {
    if (!isMentionOpen) {
      mentionAnchorRectRef.current = null;
      return;
    }

    const virtualReference = {
      getBoundingClientRect() {
        const live = getCaretBoundingClientRect();
        if (live) {
          mentionAnchorRectRef.current = live;
          return live;
        }
        const cached = mentionAnchorRectRef.current;
        if (cached) {
          return cached;
        }
        const el = draftEditorContainerRef.current;
        if (el) {
          const r = el.getBoundingClientRect();
          return new DOMRect(r.left + 8, r.bottom + 4, 0, 0);
        }
        return new DOMRect(0, 0, 0, 0);
      },
      contextElement: draftEditorContainerRef.current ?? undefined,
    };

    refs.setReference(virtualReference);
    update();
  }, [isMentionOpen, editorState, refs, update]);

  const mentionMenuFloating =
    typeof document !== 'undefined' && isMentionOpen ? (
      <FloatingPortal>
        <div
          ref={refs.setFloating}
          className={css.mentionDropdownPortal}
          style={{
            ...floatingStyles,
            zIndex: 10000,
          }}
          onMouseDown={(e) => {
            // Keeps Draft selection/focus so the first click selects reliably (no jump).
            e.preventDefault();
          }}
        >
          <Menu
            options={mentionUsers.map((user) => ({
              // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
              key: user.id,
              // @ts-expect-error - TS2339 - Property 'label' does not exist on type 'never'.
              label: user.label,
              // @ts-expect-error - TS2339 - Property 'fullName' does not exist on type 'never'.
              fullName: user.fullName,
            }))}
            // @ts-expect-error - TS2769 - No overload matches this call.
            onSelect={handleMentionSelect}
            testId={generateTestId({
              base: testId,
              slot: 'rich-text-editor-mention-dropdown',
            })}
          />
        </div>
      </FloatingPortal>
    ) : null;

  return (
    <>
      <div
        className={classify(
          css.parentContainer,
          classNames?.richTextEditorContainer,
        )}
        style={{
          '--textArea-height': height,
          '--editor-border-radius': borderRadius,
          '--textArea-bg-color': bgColor,
          '--mention-border-radius': mentionBorderRadius,
        }}
        data-testid={generateTestId({
          base: testId,
          slot: 'rich-text-editor-parent-container',
        })}
      >
        {label && (
          <div
            className={css.infoContent}
            data-testid={generateTestId({
              base: testId,
              slot: 'rich-text-editor-info-content',
            })}
          >
            <FormLabelSmall
              color="secondary"
              testId={generateTestId({
                base: testId,
                slot: 'rich-text-editor-info-content-label',
              })}
            >
              {label}
            </FormLabelSmall>
            &nbsp;
            {required && (
              <FormLabelSmall
                color="danger"
                testId={generateTestId({
                  base: testId,
                  slot: 'rich-text-editor-info-content-required',
                })}
              >
                {'*'}
              </FormLabelSmall>
            )}
          </div>
        )}
        <div
          className={classify(css.container, classNames?.richTextEditorContent)}
        >
          <div
            ref={draftEditorContainerRef}
            className={classify(
              css.draftEditorContainer,
              classNames?.draftEditorContainer,
            )}
            onClick={onFocus}
            data-testid={generateTestId({
              base: testId,
              slot: 'rich-text-editor-container',
            })}
          >
            {/* @ts-expect-error - TS2786 - 'Editor' cannot be used as a JSX component. */}
            <Editor
              editorState={editorState}
              onChange={handleDraftChange}
              handleKeyCommand={handleKeyCommand}
              placeholder={placeholder}
              tabIndex={0}
              textAlignment={textAlign}
              readOnly={readOnly || disabled}
              // @ts-expect-error - TS2769 - No overload matches this call.
              ref={editorRef}
              handleBeforeInput={handleBeforeInput}
              keyBindingFn={keyBindingFn}
              spellCheck
              data-testid={generateTestId({
                base: testId,
                slot: 'rich-text-editor-draft-editor',
              })}
            />
          </div>
        </div>
        {mentionMenuFloating}
        {!readOnly && (
          <div
            className={classify(css.actions, classNames?.richTextEditorActions)}
          >
            <div className={css.actionsContainer}>
              {visibleActionButtons.map((buttonData, index) => (
                <ActionButton
                  key={index}
                  {...buttonData}
                  disabled={disabled}
                  testId={generateTestId({
                    base: testId,
                    slot: 'rich-text-editor-action-button',
                    index,
                  })}
                />
              ))}
              {isPlaceholderEnabled && (
                <Tooltip
                  body={<span>{'Placeholder'}</span>}
                  placement="top"
                  delayMotionDuration="fast"
                  testId={generateTestId({
                    base: testId,
                    slot: 'rich-text-editor-placeholder-tooltip',
                  })}
                >
                  <ButtonDropdown
                    iconRightName="brackets-curly"
                    iconRightType="regular"
                    testId={generateTestId({
                      base: testId,
                      slot: 'rich-text-editor-placeholder-button-dropdown',
                    })}
                    menu={{
                      groupTitleOptions: PLACEHOLDER_LIST.map((item) => {
                        return {
                          groupTitle: (
                            <div>
                              <FormLabelSmall color="tertiary">
                                {item.name}
                              </FormLabelSmall>
                              {item.tooltip && (
                                <div>
                                  <Tooltip
                                    body={item.tooltip}
                                    elevation="modal"
                                  >
                                    <Icon
                                      color="tertiary"
                                      name="circle-info"
                                      size="small"
                                    />
                                  </Tooltip>
                                </div>
                              )}
                            </div>
                          ),
                          options: item.placeHolders.map((placeholder) => {
                            return {
                              key: placeholder,
                              label: placeholder.replace(/[{}]/g, ''),
                            };
                          }),
                        };
                      }),
                      isFluid: false,
                      menuDisabled: false,
                      size: 'medium',
                      allowSearch: true,
                    }}
                    onOptionSelect={(option) => {
                      handleDraftChange(
                        insertTextAtFocus(editorState, option.key),
                      );
                      // Ensure editor keeps focus after inserting placeholder
                      setTimeout(() => {
                        if (editorRef.current) {
                          // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
                          editorRef.current.focus();
                        }
                      }, 0);
                    }}
                    size="small"
                    type="ghost"
                  ></ButtonDropdown>
                </Tooltip>
              )}
            </div>
            {showActionButton && (
              <Button
                // @ts-expect-error - TS2769 - No overload matches this call.
                type={actionButtonType}
                iconLeftName={actionButtonIcon}
                onClick={() => handleButtonAction()}
                disabled={disabled}
                testId={generateTestId({
                  base: testId,
                  slot: 'rich-text-editor-action-button',
                })}
              >
                {actionButtonText}
              </Button>
            )}
          </div>
        )}
      </div>
      <Modal
        isOpen={isLinkModalOpen}
        onClose={() => handleCloseLinkModal()}
        classNames={{content: css.modalContent}}
        size="large"
        testId={generateTestId({
          base: testId,
          slot: 'rich-text-editor-link-modal',
        })}
      >
        <ModalHeader
          onCloseButtonClick={() => handleCloseLinkModal()}
          testId={generateTestId({
            base: testId,
            slot: 'rich-text-editor-link-modal-header',
          })}
        >
          <SubTitleLarge>Enter url for the selected text</SubTitleLarge>
        </ModalHeader>
        <ModalBody
          testId={generateTestId({
            base: testId,
            slot: 'rich-text-editor-link-modal-body',
          })}
        >
          <Input
            testId={generateTestId({
              base: testId,
              slot: 'rich-text-editor-link-modal-input',
            })}
            label="Enter the url"
            placeholder="url"
            size="medium"
            type="url"
            value={linkTextValue}
            onChange={(e) => setLinkTextValue(e.target.value)}
            onKeyDown={onLinkInputKeyDown}
            pattern="((http:\/\/|https:\/\/|www\.)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?)?"
            error={!!linkTextValue && !isValidUrl(linkTextValue)}
            errorText={
              'Please enter a valid URL starting with http://, https://, or www.'
            }
          />
        </ModalBody>
        <ModalFooter
          testId={generateTestId({
            base: testId,
            slot: 'rich-text-editor-link-modal-footer',
          })}
        >
          <Button
            testId={generateTestId({
              base: testId,
              slot: 'rich-text-editor-link-modal-button',
            })}
            type="primary"
            disabled={!linkTextValue || !isValidUrl(linkTextValue)}
            onClick={confirmSelectedLink}
          >
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default RichTextEditor;
